import React from 'react';
import './skeleton-loader.scss';
import classNames from 'classnames';

export type SkeletonLoaderProps = {
  testId?: string;
  count?: number;
  width?: string;
  height?: string;
  circle?: boolean;
  isRightAligned?: boolean;
  isCenterAligned?: boolean;
  marginBottom?: string;
  marginRight?: string;
  marginTop?: string;
  marginLeft?: string;
  borderRadius?: string;
  classNameInner?: string;
};

/* Example usages:
  <SkeletonLoaderWrapper>
    <SkeletonLoader width="300px" height="180px" />
    <SkeletonLoader width="30px" circle />
    <SkeletonLoader count={2} />
    <SkeletonLoader width="50%" />
  </SkeletonLoaderWrapper>
  ......
  <SkeletonLoaderWrapper>
    <SkeletonLoader width="15%" marginBottom="1.5rem" />
  </SkeletonLoaderWrapper>
  <SkeletonLoaderWrapper>
    <SkeletonLoader width="27%" />
    <SkeletonLoader width="6%" />
    <SkeletonLoader width="10%" isRightAligned />
  </SkeletonLoaderWrapper>
  <SkeletonLoaderWrapper>
    <SkeletonLoader height="600px" />
  </SkeletonLoaderWrapper>
  ......
  <SkeletonLoaderWrapper className="custom-class">
    <SkeletonLoader marginBottom='' />
  </SkeletonLoaderWrapper>
*/
export const SkeletonLoaderWrapper = ({
  testId = 'skeleton-loader-wrapper',
  className = '',
  children,
}: {
  testId?: string;
  className?: string;
  children?: React.ReactNode;
}) => {
  const classText = 'skeleton-loader-wrapper';
  const wrapperClass = className ? `${className} ${classText}` : classText;

  return (
    <div data-testid={testId} className={wrapperClass}>
      {children}
    </div>
  );
};

export const SkeletonLoader = ({
  testId = 'skeleton-loader',
  // `count` can help in creating multiple skeleton lines
  count = 1,
  // Any CSS units can be added for `width`
  width = '100%',
  // Any CSS units can be added for `height`
  height = '20px',
  // To create circle or avatars, use `circle` prop. If using this, make sure to
  // provide either `width` or `height` prop. If both are present, height is used
  circle = false,
  isRightAligned = false,
  isCenterAligned = false,
  /* Default margin bottom between the items is 1rem */
  marginBottom = '1rem',
  marginTop,
  marginRight,
  marginLeft,
  borderRadius,
  classNameInner = '',
}: SkeletonLoaderProps) => {
  const loaderItems = [];
  const style: any = {};
  let className = 'skeleton-loader';

  if (width) {
    style.width = width;
  }

  if (height) {
    style.height = height;
  }

  if (isRightAligned) {
    className += ' skeleton-loader-right';
  } else if (isCenterAligned) {
    className += ' skeleton-loader-center';
  } else {
    className += ' skeleton-loader-left';
  }

  if (marginBottom) {
    style.marginBottom = marginBottom;
  }

  if (marginTop) {
    style.marginTop = marginTop;
  }

  if (marginLeft) {
    style.marginLeft = marginLeft;
  }

  if (marginRight) {
    style.marginRight = marginRight;
  }

  if (borderRadius) {
    style.borderRadius = borderRadius;
  }

  // Make circle based on either width or height
  if ((width || height) && circle) {
    // If both width and height are present, height is used
    const commonVal = height || width;

    style.width = commonVal;
    style.minWidth = commonVal;
    style.height = commonVal;
    style.borderRadius = '50%';
  }

  for (let i = 0; i < count; i++) {
    // Note: Since, we are not modifying our list, using index as key
    loaderItems.push(
      <div key={i} className={classNames(className, classNameInner)} style={style} data-testid={testId} />
    );
  }

  return <>{loaderItems}</>;
};
